import HTTPRequest from '../HTTPRequest';
import {
    getTeamProviderId,
    getTeamCustomerId,
    getTeamUpAccessToken,
    getTeamUpRefreshToken,
    getTeamUpTokenExpiry,
} from '../Utilities';
import { setCookie, deleteCookie } from '../../../../tools/cookies';

export default class BaseRequest {
    /**
     * Need to set the class name as used in forward request and name is lost when minified
     *
     * @param {string}
     */
    className;

    /**
     * Base API endpoint used in all request which should exclude the API base
     *
     * @param {string}
     */
    endpoint;

    /**
     * Default headers required in API request
     *
     * @param {Objecct}
     */
    headers = {};

    /**
     * Should be either "provider" or "customer".
     * Customer mode requests apply customer site rules and are more restricted.
     *
     * @param {string}
     */
    requestMode;

    /**
     * The ID of the provider account the API request is for
     *
     * @param {string}
     */
    providerId;

    /**
     * The ID of the customer account the API request is for
     *
     * @param {string}
     */
    customerId;

    /**
     * The access token usedin the API request
     *
     * @param {string}
     */
    accessToken;

    /**
     * Expiry time for the auth cookies (in days)
     *
     * @param {integer}
     */
    cookieExpiry = 30;

    /**
     * Whether to add the access token to the headers
     *
     * @param {boolean}
     */
    auth = true;

    /**
     * Whether to forward the request to PHP which is require the access token to the headers
     *
     * @param {boolean}
     */
    fowardRequest = false;

    /**
     * BaseRequest constructor
     *
     * @param {string} requestMode
     * @param {string} providerId
     * @param {string} customerId
     * @param {boolean} auth
     * @param {boolean} fowardRequest
     */
    constructor(
        requestMode = 'customer',
        providerId = '',
        customerId = '',
        auth = true,
        fowardRequest = false,
    ) {
        let headers = {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'TeamUp-Client-ID': `${process.env.TEAM_UP_CLIENT_ID}`,
        };

        if (`${process.env.TEAM_UP_ENABLE_TEST_FEATURES}` === 'true') {
            headers['Teamup-BE-Target'] = 'next';
        }

        const teamUpProviderId = getTeamProviderId();
        if (
            providerId !== false &&
            providerId == '' &&
            teamUpProviderId != ''
        ) {
            providerId = teamUpProviderId;
        }

        const teamUpCustomerId = getTeamCustomerId();
        if (
            customerId !== false &&
            customerId == '' &&
            teamUpCustomerId != ''
        ) {
            customerId = teamUpCustomerId;
        }

        // All provider requests need to be forwarded to PHP
        if (requestMode == 'provider') {
            fowardRequest = true;
        }

        this.setAuth(auth);
        this.setForwardRequest(fowardRequest);
        this.setHeaders(headers);
        this.setRequestMode(requestMode);

        if (providerId !== false) {
            this.setProviderId(providerId);
        }

        if (auth) {
            if (customerId !== false) {
                this.setCustomerId(customerId);
            }

            if (requestMode == 'customer') {
                const teamUpAccessToken = getTeamUpAccessToken();

                if (teamUpAccessToken != '') {
                    this.setAccessToken(teamUpAccessToken);
                }
            }
        }
    }

    /**
     * @param {string} className
     * @return {string}
     */
    setClassName(className) {
        this.className = className;
        return this.className;
    }

    /**
     * @return {string}
     */
    getClassName() {
        return this.className;
    }

    /**
     * @param {string} endpoint
     * @return {string}
     */
    setEndpoint(endpoint) {
        this.endpoint = endpoint;
        return this.endpoint;
    }

    /**
     * @return {string}
     */
    getEndpoint() {
        return this.endpoint;
    }

    /**
     * @param {object} headers
     * @return {object}
     */
    setHeaders(headers) {
        this.headers = { ...this.headers, ...headers };
        return this.headers;
    }

    /**
     * @return {array}
     */
    getHeaders() {
        return this.headers;
    }

    /**
     * @param {string} requestMode
     * @return {string}
     */
    setRequestMode(requestMode) {
        this.requestMode = requestMode;
        this.setHeaders({ 'Teamup-Request-Mode': requestMode });
        return this.requestMode;
    }

    /**
     * @return {string}
     */
    getRequestMode() {
        return this.requestMode;
    }

    /**
     * @param {string} providerId
     * @return {string}
     */
    setProviderId(providerId = '') {
        this.providerId = providerId;
        this.setHeaders({ 'Teamup-Provider-ID': providerId });
        return this.providerId;
    }

    /**
     * @return {string}
     */
    getProviderId() {
        return this.providerId;
    }

    /**
     * @param {string} customerId
     * @return {string}
     */
    setCustomerId(customerId = '') {
        this.customerId = customerId;
        this.setHeaders({ 'Teamup-Customer-ID': customerId });
        return this.customerId;
    }

    /**
     * @return {string}
     */
    getCustomerId() {
        return this.customerId;
    }

    /**
     * @param {string} cookieExpiry
     * @return {string}
     */
    setCookieExpiry(cookieExpiry) {
        this.cookieExpiry = cookieExpiry;
        return this.cookieExpiry;
    }

    /**
     * @return {string}
     */
    getCookieExpiry() {
        return this.cookieExpiry;
    }

    /**
     * @param {string} accessToken
     * @return {string}
     */
    setAccessToken(accessToken) {
        this.accessToken = accessToken;
        this.setHeaders({ Authorization: `Token ${accessToken}` });
        return this.accessToken;
    }

    /**
     * @return {string}
     */
    getAccessToken() {
        return this.accessToken;
    }

    /**
     * @param {boolean} auth
     * @return {boolean}
     */
    setAuth(auth) {
        this.auth = auth;
        return this.auth;
    }

    /**
     * @return {boolean}
     */
    getAuth() {
        return this.auth;
    }

    /**
     * @param {boolean} fowardRequest
     * @return {boolean}
     */
    setForwardRequest(fowardRequest) {
        this.fowardRequest = fowardRequest;
        return this.fowardRequest;
    }

    /**
     * @return {boolean}
     */
    getForwardRequest() {
        return this.fowardRequest;
    }

    /**
     * @param {string} endpoint
     * @param {string} action
     * @param {object} parameters
     * @return {HTTPRequest}
     */
    buildForwardRequest(endpoint = '', action = '', parameters = {}) {
        const request = new HTTPRequest(
            'POST',
            `forward-request`,
            this.getHeaders(),
            {},
            {
                endpoint: endpoint,
                action: action,
                parameters: parameters,
            },
        );
        request.setHeaders({
            Authorization: `Basic ${process.env.WEB_API_TOKEN}`,
        });
        request.setBaseUrl(`${process.env.WEB_API_ENDPOINT}team-up/`);
        if (this.getRequestMode() === 'customer') {
            request.setHeaders({
                'Teamup-Access-Token': `${this.getAccessToken()}`,
            });
        }

        return request;
    }

    /**
     * @param {string} error
     * @return {void}
     */
    logError(error) {
        console.log(
            `${process.env.TEAM_UP_API_BASE}${this.getEndpoint()}: ${error}`,
        );
    }

    /**
     * @param {string} returnTo
     * @param {object} query
     * @return {string}
     */
    buildReturnUrl(returnTo = '', query = {}) {
        if (returnTo == '') {
            returnTo = process.env.TEAM_UP_CHECKOUT_SUCCESS_PAGE;
        }

        let returnToUrl = new URL(returnTo);
        if (Object.keys(query).length > 0) {
            for (const key in query) {
                if (query[key] != '') {
                    returnToUrl.searchParams.set(key, query[key]);
                }
            }
        }

        return returnToUrl.toString();
    }

    /**
     * @param {object} response
     * @return {void}
     */
    setTokens(response) {
        if ('access_token' in response) {
            setCookie(
                'team-up-access-token',
                response.access_token,
                this.getCookieExpiry(),
            );
        }

        if ('refresh_token' in response) {
            setCookie(
                'team-up-refresh-token',
                response.refresh_token,
                this.getCookieExpiry(),
            );
        }

        if ('expires_in' in response) {
            const tokenExpiry = new Date();

            // response.expires_in is returned in seconds so we need to convert to milliseconds
            // in order to add correctly to the current time.
            tokenExpiry.setTime(
                tokenExpiry.getTime() + response.expires_in * 1000,
            );

            // We need to store the expiry time in seconds in order to match correctly with other
            // date comparisions set in PHP cookie process for comparison.
            setCookie(
                'team-up-token-expires',
                Math.round(tokenExpiry.getTime() / 1000),
                this.getCookieExpiry(),
            );
        }
    }

    /**
     * @return {boolean}
     */
    isTokenExpired() {
        let expired = true;

        const teamUpTokenExpiry = getTeamUpTokenExpiry();

        if (teamUpTokenExpiry != '') {
            const now = new Date();
            const tokenExpiryMilliseconds = parseInt(teamUpTokenExpiry) * 1000;
            const tokenExpiry = new Date(tokenExpiryMilliseconds);

            expired = now > tokenExpiry;
        }

        return expired;
    }

    /**
     * @param {string} token
     * @return {object}
     */
    async refreshToken(token = '') {
        const teamUpToken = getTeamUpRefreshToken();
        if (token == '' && teamUpToken != '') {
            token = teamUpToken;
        }

        let request = new HTTPRequest(
            'POST',
            'auth/refresh_access_token',
            this.getHeaders(),
            {},
            {
                new_access_token_type: this.getRequestMode(),
                refresh_token: token,
            },
        );
        if (this.getForwardRequest()) {
            request = this.buildForwardRequest('Auth', 'refreshToken', {
                token: token,
            });
        }

        let response = await fetch(request.buildUrl(), request.buildPayload());
        response = await response.json();
        response = request.handleResponse(response);

        if (response.results.length > 0) {
            this.setTokens(response.results[0]);
            if ('access_token' in response) {
                this.setAccessToken(response.results[0].access_token);
            }
        }

        return response;
    }

    /**
     * @param {string} redirectType (soft|hard)
     * @return {void}
     */
    forceLogout(redirectType = 'hard') {
        deleteCookie('team-up-access-token');
        deleteCookie('team-up-refresh-token');
        deleteCookie('team-up-token-expires');
        deleteCookie('team-up-customer-id');
        deleteCookie('team-up-provider-id');
        deleteCookie('team-up-venue-id');
        deleteCookie('team-up-customer-first-name');

        // Use soft logout when in my account area as account wrapper will pickup history
        // state change and redirect automatically. Elsewhere use hard to force redirect.
        if (redirectType == 'soft') {
            // Set state flag to allow account redirect to pass error to login page
            history.pushState({ redirectWithAuthError: true }, '');
        } else {
            // Note: Hard logout is intended for when user account is no logger available
            // If this needs to be seperated out in the future we can do so with an additional parameter.
            window.location.href = '/login/?error=auth_expired';
        }
    }

    /**
     * @param {integer} page
     * @param {integer} perPage
     * @param {array} expand
     * @param {array} fields
     * @param {string} sort
     * @param {object} filters
     * @param {boolean} fetchAll
     * @return {object}
     */
    async list(
        page = 1,
        perPage = 20,
        expand = [],
        fields = [],
        sort = '',
        filters = {},
        fetchAll = false,
    ) {
        let request = new HTTPRequest(
            'GET',
            this.getEndpoint(),
            this.getHeaders(),
            {
                ...{
                    expand: expand,
                    fields: fields,
                    sort: sort,
                },
                ...filters,
            },
            {},
            perPage,
        );
        if (this.getForwardRequest()) {
            request = this.buildForwardRequest(this.getClassName(), 'list', {
                page: page,
                perPage: perPage,
                expand: expand,
                fields: fields,
                sort: sort,
                filters: filters,
                fetchAll: fetchAll,
            });
        }

        return await request.sendRequestPaginated(page, fetchAll);
    }

    /**
     * @param {integer} perPage
     * @param {array} expand
     * @param {array} fields
     * @param {string} sort
     * @param {object} filters
     * @return {object}
     */
    async listAll(
        perPage = 20,
        expand = [],
        fields = [],
        sort = '',
        filters = {},
    ) {
        return await this.list(1, perPage, expand, fields, sort, filters, true);
    }

    /**
     * @param {integer} id
     * @param {array} expand
     * @param {array} fields
     * @return {object}
     */
    async retrieve(id, expand = [], fields = []) {
        let request = new HTTPRequest(
            'GET',
            `${this.getEndpoint()}/${id}`,
            this.getHeaders(),
            {
                expand: expand,
                fields: fields,
            },
        );
        if (this.getForwardRequest()) {
            request = this.buildForwardRequest(
                this.getClassName(),
                'retrieve',
                {
                    id: id,
                    expand: expand,
                    fields: fields,
                },
            );
        }

        return await request.sendRequest();
    }

    /**
     * @param {object} data
     * @return {object}
     */
    async create(data) {
        let request = new HTTPRequest(
            'POST',
            this.getEndpoint(),
            this.getHeaders(),
            {},
            data,
        );
        if (this.getForwardRequest()) {
            request = this.buildForwardRequest(this.getClassName(), 'create', {
                data: data,
            });
        }

        return await request.sendRequest();
    }

    /**
     * @param {integer} id
     * @param {object} data
     * @return {object}
     */
    async update(id, data) {
        let request = new HTTPRequest(
            'PUT',
            `${this.getEndpoint()}/${id}`,
            this.getHeaders(),
            {},
            data,
        );
        if (this.getForwardRequest()) {
            request = this.buildForwardRequest(this.getClassName(), 'update', {
                data: data,
            });
        }

        return await request.sendRequest();
    }

    /**
     * @param {integer} id
     * @param {object} data
     * @return {object}
     */
    async partialUpdate(id, data) {
        let request = new HTTPRequest(
            'PATCH',
            `${this.getEndpoint()}/${id}`,
            this.getHeaders(),
            {},
            data,
        );
        if (this.getForwardRequest()) {
            request = this.buildForwardRequest(
                this.getClassName(),
                'partialUpdate',
                { data: data },
            );
        }

        return await request.sendRequest();
    }

    /**
     * @param {integer} id
     * @param {object} data
     * @return {object}
     */
    async delete(id, data) {
        let request = new HTTPRequest(
            'DELETE',
            `${this.getEndpoint()}/${id}`,
            this.getHeaders(),
            {},
            data,
        );
        if (this.getForwardRequest()) {
            request = this.buildForwardRequest(this.getClassName(), 'delete', {
                data: data,
            });
        }

        return await request.sendRequest();
    }
}
